import React from "react";

type CornerStyling = {
  tooltipClass: string;
  tooltipCornerClass: string;
};

type TooltipDirection = Record<
  "TOP" | "LEFT" | "RIGHT" | "BOTTOM",
  CornerStyling
>;

export const TooltipDirections: TooltipDirection = {
  TOP: {
    tooltipClass:
      "origin-bottom translate-x-[-50%] left-[50%] bottom-[calc(100%_+_10px)]",
    tooltipCornerClass: "rotate-[-135deg] bottom-[-8px]",
  }, // The tooltip would display above the trigger
  RIGHT: {
    tooltipClass:
      "origin-left translate-y-[-50%] top-[50%] left-[calc(100%_+_10px)]",
    tooltipCornerClass: "rotate-[-45deg] left-[-8px]",
  }, // The tooltip would display to the right of the trigger
  BOTTOM: {
    tooltipClass:
      "origin-top translate-x-[-50%] left-[50%] top-[calc(100%_+_10px)]",
    tooltipCornerClass: "rotate-[45deg] top-[-8px]",
  }, // The tooltip would display below the trigger
  LEFT: {
    tooltipClass:
      "origin-right translate-y-[-50%] top-[50%] right-[calc(100%_+_10px)]",
    tooltipCornerClass: "rotate-[135deg] right-[-8px]",
  }, // The tooltip would display to the right of the trigger
};

type ModernTooltipProps = {
  tooltipDirection?: CornerStyling;
  delayInMs?: number;
  durationInMs?: number;
  children: React.ReactNode;
  tooltipContent: React.ReactNode;
  tooltipWidth?: number;
  openOnFocus?: boolean;
  renderAsButton?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const ModernTooltip = ({
  tooltipDirection = TooltipDirections.TOP,
  durationInMs,
  tooltipWidth,
  children,
  tooltipContent,
  openOnFocus = false,
  renderAsButton = false,
  ...props
}: ModernTooltipProps) => {
  return (
    <div
      {...props}
      className={`relative ${props.className} group tooltip-parent`}
      style={{ zIndex: 100, ...(props.style ?? {}) }}
      tabIndex={renderAsButton ? 0 : -1}
      role="tooltip"
    >
      {children}

      <div
        className={`tooltip absolute bg-background border border-border rounded-xl shadow-md pointer-events-none transition opacity-0 blur-md scale-[0] group-hover:opacity-100 group-hover:scale-100 group-hover:blur-0 ${
          openOnFocus
            ? "group-focus-within:opacity-100 group-focus-within:scale-100 group-focus-within:blur-0"
            : ""
        } ${tooltipDirection.tooltipClass}`}
        style={{
          transitionDuration: `${durationInMs ?? 400}ms`,
        }}
      >
        <div
          className="tooltip-inner relative flex flex-col items-center justify-center py-[6px] px-[10px] text-foreground text-sm"
          style={{ width: tooltipWidth ? `${tooltipWidth}px` : "" }}
        >
          {tooltipContent}
          <div
            className={`tooltip-corner absolute h-[14px] w-[14px] border border-border bg-background border-r-0 border-b-0 rounded-tl-md ${tooltipDirection.tooltipCornerClass}`}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default ModernTooltip;
